import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReportTemplate from '../components/ReportTemplate';
import TemplateSelector from '../components/TemplateSelector';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { supabase } from '../lib/supabase';

/* ─── Palette flat ─── */
const C = {
  bg: '#f5f5f3',
  card: '#ffffff',
  border: '#e5e5e5',
  text: '#1a1a1a',
  text2: '#52525b',
  text3: '#a1a1aa',
  purple: '#6366f1',
  purpleBg: '#eef2ff',
  green: '#1D9E75',
  greenBg: '#e6f5ef',
  red: '#e55353',
  meta: { bg: 'rgba(24,119,242,0.10)', color: '#1877f2' },
  google: { bg: 'rgba(66,133,244,0.10)', color: '#4285f4' },
  tiktok: { bg: 'rgba(0,0,0,0.06)', color: '#000000' },
};

const font = "system-ui, -apple-system, 'Segoe UI', sans-serif";

/* ─── Stili ─── */
const s = {
  page: { padding: '32px 32px', background: C.bg, minHeight: '100vh', fontFamily: font, color: C.text },
  title: { fontSize: 21, fontWeight: 700, color: C.text, letterSpacing: '-0.3px', margin: 0, marginBottom: 4 },
  subtitle: { fontSize: 13, color: C.text2, margin: 0, marginBottom: 24, fontWeight: 400 },

  steps: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32, gap: 0 },
  stepItem: { display: 'flex', alignItems: 'center', gap: 8, flex: 'none' },
  stepLine: { flex: '0 0 80px', height: 1, background: C.border, margin: '0 16px' },
  stepNum: (state) => ({
    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 600, flexShrink: 0, transition: 'all 0.15s',
    ...(state === 'done'   ? { background: C.greenBg, color: C.green, border: `1.5px solid ${C.green}` } :
        state === 'active' ? { background: C.purpleBg, color: C.purple, border: `1.5px solid ${C.purple}` } :
                             { background: C.card, color: C.text3, border: `1.5px solid ${C.border}` }),
  }),
  stepLabel: (state) => ({
    fontSize: 13, fontWeight: 600, lineHeight: '28px',
    color: state === 'done' ? C.green : state === 'active' ? C.purple : C.text3,
  }),

  section: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '24px 24px', marginBottom: 16 },
  sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 16, display: 'block' },

  chips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: (sel) => ({
    padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, fontWeight: 500, transition: 'all 0.12s', userSelect: 'none',
    border: `1px solid ${sel ? C.purple : C.border}`,
    background: sel ? C.purpleBg : C.card,
    color: sel ? C.purple : C.text2,
  }),
  initials: (sel) => ({
    width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 700, flexShrink: 0,
    background: sel ? 'rgba(99,102,241,0.18)' : '#f0f0ee',
    color: sel ? C.purple : C.text2,
  }),

  titleInput: { width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 12px', fontSize: 13, color: C.text, fontFamily: font, outline: 'none', boxSizing: 'border-box', height: 40 },

  dateGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  dateLabel: { fontSize: 12, fontWeight: 600, color: C.text2, whiteSpace: 'nowrap' },
  dateInput: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 12px', fontSize: 13, color: C.text, fontFamily: font, outline: 'none', width: 140, height: 36, boxSizing: 'border-box' },
  shortcuts: { display: 'flex', gap: 8, flexWrap: 'nowrap', alignItems: 'center' },
  scut: (active) => ({
    padding: '0 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: font,
    height: 36, minWidth: 72, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${active ? C.purple : '#d1d5db'}`,
    background: active ? C.purple : C.card,
    color: active ? '#ffffff' : '#374151',
  }),

  campaignTable: { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' },
  tableHeader: { padding: '12px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', borderBottom: `2px solid ${C.border}`, textAlign: 'left', background: C.bg },
  campaignRow: (sel) => ({ borderBottom: `1px solid #f3f4f6`, cursor: 'pointer', background: sel ? C.purpleBg : C.card, transition: 'background 0.1s' }),
  campaignCell: { padding: '0 12px', fontSize: 13, color: C.text2, height: 56, verticalAlign: 'middle' },
  campaignName: { padding: '0 12px', fontSize: 13, color: C.text, fontWeight: 500, height: 56, verticalAlign: 'middle' },
  checkBox: (checked) => ({
    width: 18, height: 18, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer',
    border: `1.5px solid ${checked ? C.purple : '#d1d5db'}`,
    background: checked ? C.purple : C.card,
  }),
  platformBadge: (platform) => ({
    padding: '3px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'inline-block',
    background: platform === 'meta' ? C.meta.bg : platform === 'google' ? C.google.bg : C.tiktok.bg,
    color: platform === 'meta' ? C.meta.color : platform === 'google' ? C.google.color : C.tiktok.color,
  }),
  numericCell: { padding: '0 12px', fontSize: 13, color: C.text2, height: 56, verticalAlign: 'middle', textAlign: 'right', fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', monospace", fontVariantNumeric: 'tabular-nums' },

  ctaBtn: (disabled) => ({
    width: '100%', padding: '12px', borderRadius: 8, border: 'none',
    background: disabled ? '#a5b4fc' : C.purple,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 14, fontWeight: 600, color: 'white', fontFamily: font,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16,
  }),
  primBtn: { padding: '10px 20px', borderRadius: 6, border: 'none', background: C.purple, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'white', fontFamily: font },
  secBtn: { padding: '10px 20px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: C.text2, fontFamily: font },
  actionRow: { display: 'flex', gap: 8, marginTop: 16 },

  filterPill: (active) => ({
    padding: '0 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: font,
    height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 64, whiteSpace: 'nowrap',
    border: `1px solid ${active ? C.purple : '#d1d5db'}`,
    background: active ? C.purple : C.card,
    color: active ? '#ffffff' : '#374151',
  }),
  searchInput: { flex: 1, minWidth: 120, background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 12px', fontSize: 12, color: C.text, fontFamily: font, outline: 'none', height: 32, boxSizing: 'border-box' },

  loadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: 16 },
  spinner: { width: 32, height: 32, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.purple}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};

/* ─── Fix unicode escapes ─── */
const fixUnicode = (html) => {
  if (!html) return html;
  return html.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
};

/* ─── Helpers ─── */
function getInitials(name) {
  return name.split(' ').map(w => w[0]).join('').substring(0, 3).toUpperCase();
}
function formatCurrency(v) {
  if (!v && v !== 0) return '—';
  return '€' + Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatNumber(v) {
  if (!v && v !== 0) return '—';
  return Number(v).toLocaleString('it-IT');
}

/* ─── Component ─── */
export default function Generator() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2026-12-31');
  const [activeShortcut, setActiveShortcut] = useState(2026);
  const [reportTitle, setReportTitle] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [campaignInsights, setCampaignInsights] = useState({});
  const [adsetRows, setAdsetRows] = useState([]);

  const [dailyData, setDailyData] = useState([]);
  const [campaignImage, setCampaignImage] = useState(null);
  const [mockupImageUrl, setMockupImageUrl] = useState(null);
  const [demographics, setDemographics] = useState({ gender: [], age: [] });
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [onlyWithData, setOnlyWithData] = useState(true);
  const [visibleKpis, setVisibleKpis] = useState(['spend', 'impressions', 'clicks']);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [report, setReport] = useState('');
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [campaignAds, setCampaignAds] = useState({});
  const [loadingAds, setLoadingAds] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);

  async function handleImageUpload(campaign, file) {
    if (!file) return;
    setUploadingImage(campaign.id);
    try {
      const resized = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1200;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          canvas.toBlob(resolve, 'image/jpeg', 0.85);
        };
        img.src = URL.createObjectURL(file);
      });
      const path = `manual/${campaign.id}.jpg`;
      const { error } = await supabase.storage
        .from('campaign-media')
        .upload(path, resized, { upsert: true, contentType: 'image/jpeg' });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from('campaign-media').getPublicUrl(path);
      await supabase.from('campaigns').update({ preview_image_url: urlData.publicUrl }).eq('id', campaign.id);
      setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, preview_image_url: urlData.publicUrl } : c));
    } catch(e) {
      alert('Errore upload: ' + e.message);
    } finally {
      setUploadingImage(null);
    }
  }

  const visibleCampaigns = useMemo(() => {
    return [...campaigns]
      .filter(c => {
        if (!c.start_date) return (campaignInsights[c.id]?.spend||0) > 0;
        const start = new Date(c.start_date);
        const end = c.end_date ? new Date(c.end_date) : null;
        return start <= new Date(dateTo) && (!end || end >= new Date(dateFrom));
      })
      .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (campaignInsights[b.id]?.spend||0) - (campaignInsights[a.id]?.spend||0))
      .slice(0, pageSize);
  }, [campaigns, search, pageSize, campaignInsights, dateFrom, dateTo]);

  async function loadAllInsightsRange(from, to) {
    // Ricarica insights con range date specifico (per campagne con date fuori dal filtro)
    const client = selectedClient;
    if (!client) return;
    try {
      const { data: insights } = await supabase
        .from('adset_insights_daily').select('adset_id, spend, impressions, clicks, reach, inline_link_clicks, post_reactions')
        .eq('client_id', client.id)
        .gte('date', from)
        .lte('date', to)
        .limit(10000);
      if (!insights?.length) return;
      const adsetIds = [...new Set(insights.map(i => i.adset_id))];
      const CHUNK2 = 200;
      const allAdsetData2 = [];
      for (let i = 0; i < adsetIds.length; i += CHUNK2) {
        const chunk = adsetIds.slice(i, i + CHUNK2);
        const { data: chunkData } = await supabase.from('adsets').select('id, name, campaign_id').in('id', chunk);
        allAdsetData2.push(...(chunkData || []));
      }
      const adsetNameMap = {};
      const adsetToCampaign = {};
      allAdsetData2.forEach(a => {
        adsetNameMap[a.id]    = { name: a.name || '', campaign_id: a.campaign_id };
        adsetToCampaign[a.id] = a.campaign_id;
      });
      const totals = {};
      const adsetInsightsByFilter = {};
      (insights || []).forEach(i => {
        const cid = adsetToCampaign[i.adset_id]; if (!cid) return;
        if (!totals[cid]) totals[cid] = { spend:0, impressions:0, clicks:0, reach:0, inline_link_clicks:0, post_reactions:0 };
        totals[cid].spend              += parseFloat(i.spend)||0;
        totals[cid].impressions        += parseInt(i.impressions)||0;
        totals[cid].clicks             += parseInt(i.inline_link_clicks)||0;
        totals[cid].reach              += parseInt(i.reach)||0;
        totals[cid].inline_link_clicks += parseInt(i.inline_link_clicks)||0;
        totals[cid].post_reactions     += parseInt(i.post_reactions)||0;
        const adsetName = (adsetNameMap[i.adset_id]?.name || '').toLowerCase();
        if (adsetName) {
          if (!adsetInsightsByFilter[adsetName]) adsetInsightsByFilter[adsetName] = { spend:0, impressions:0, clicks:0, reach:0, inline_link_clicks:0, post_reactions:0, campaign_ids: new Set() };
          adsetInsightsByFilter[adsetName].spend              += parseFloat(i.spend)||0;
          adsetInsightsByFilter[adsetName].impressions        += parseInt(i.impressions)||0;
          adsetInsightsByFilter[adsetName].clicks             += parseInt(i.inline_link_clicks)||0;
          adsetInsightsByFilter[adsetName].reach              += parseInt(i.reach)||0;
          adsetInsightsByFilter[adsetName].inline_link_clicks += parseInt(i.inline_link_clicks)||0;
          adsetInsightsByFilter[adsetName].post_reactions     += parseInt(i.post_reactions)||0;
          adsetInsightsByFilter[adsetName].campaign_ids.add(cid);
        }
      });
      Object.values(adsetInsightsByFilter).forEach(v => { v.campaign_ids = [...v.campaign_ids]; });
      // (già fatto sopra con __adsets__)
      // Salva adsetInsights dentro campaignInsights sotto chiave speciale
      setCampaignInsights(prev => ({ ...prev, ...totals, __adsets__: { ...(prev.__adsets__ || {}), ...adsetInsightsByFilter } }));
    } catch(e) { console.error('loadAllInsightsRange error:', e); }
  }

  async function loadAllInsights(clientOverride, fromOverride, toOverride) {
    const client = clientOverride || selectedClient;
    if (!client) return;
    const from = fromOverride || dateFrom;
    const to   = toOverride   || dateTo;
    setLoadingInsights(true);
    try {
      // RPC server-side: join adset_insights_daily + adsets in un colpo solo
      const { data: rows, error: rpcError } = await supabase.rpc('get_campaign_insights', {
        p_client_id: client.id,
        p_date_from: from,
        p_date_to:   to,
      });
      console.log('[RPC] rows:', rows?.length, '| error:', rpcError, '| client:', client.id, '| from:', from, '| to:', to);
      if (rpcError) { console.error('RPC error:', rpcError); setLoadingInsights(false); return; }
      if (!rows?.length) { setLoadingInsights(false); return; }
      // Aggrega per campagna
      const totals = {};
      (rows || []).forEach(r => {
        const cid = r.campaign_id;
        if (!totals[cid]) totals[cid] = { spend:0, impressions:0, clicks:0, all_clicks:0, reach:0, inline_link_clicks:0, post_reactions:0, engagement:0 };
        totals[cid].spend              += parseFloat(r.spend)||0;
        totals[cid].impressions        += parseInt(r.impressions)||0;
        totals[cid].clicks             += parseInt(r.inline_link_clicks)||0;
        totals[cid].all_clicks         += parseInt(r.all_clicks)||0;
        totals[cid].reach              += parseInt(r.reach)||0;
        totals[cid].inline_link_clicks += parseInt(r.inline_link_clicks)||0;
        totals[cid].post_reactions     += parseInt(r.post_reactions)||0;
        totals[cid].engagement         += parseInt(r.engagement)||0;
      });
      setCampaignInsights(prev => ({ ...prev, ...totals }));
      // Salva righe adset raw per filtraggio nel template
      setAdsetRows(rows || []);
      const { data: daily } = await supabase.from("adset_insights_daily").select("date, spend, impressions, clicks").eq("client_id", client.id).gte("date", from).lte("date", to).order("date");
      const byDay = {}; (daily||[]).forEach(r => { if (!byDay[r.date]) byDay[r.date] = {date:r.date,spend:0,impressions:0,clicks:0}; byDay[r.date].spend+=parseFloat(r.spend)||0; byDay[r.date].impressions+=parseInt(r.impressions)||0; byDay[r.date].clicks+=parseInt(r.clicks)||0; }); setDailyData(Object.values(byDay).sort((a,b)=>a.date.localeCompare(b.date)));
      const { data: demoData } = await supabase.from("adset_demographics_daily").select("gender, age_range, impressions, clicks, spend").eq("client_id", client.id).gte("date", from).lte("date", to);
      const gMap={}, aMap={}; (demoData||[]).forEach(r=>{ if(r.gender!=="ALL"){if(!gMap[r.gender])gMap[r.gender]={gender:r.gender,impressions:0,clicks:0,spend:0}; gMap[r.gender].impressions+=parseInt(r.impressions)||0; gMap[r.gender].clicks+=parseInt(r.clicks)||0; gMap[r.gender].spend+=parseFloat(r.spend)||0;} if(r.age_range!=="ALL"&&r.age_range!=="AGE_RANGE_UNDETERMINED"){if(!aMap[r.age_range])aMap[r.age_range]={age_range:r.age_range,impressions:0,clicks:0,spend:0}; aMap[r.age_range].impressions+=parseInt(r.impressions)||0; aMap[r.age_range].clicks+=parseInt(r.clicks)||0; aMap[r.age_range].spend+=parseFloat(r.spend)||0;} }); const ageOrder=["AGE_RANGE_18_24","AGE_RANGE_25_34","AGE_RANGE_35_44","AGE_RANGE_45_54","AGE_RANGE_55_64","AGE_RANGE_65_UP"]; setDemographics({gender:Object.values(gMap),age:ageOrder.map(k=>aMap[k]).filter(Boolean)});
    } catch(e) { console.error(e); }
    finally { setLoadingInsights(false); }
  }

  useEffect(() => {
    if (campaigns.length > 0 && step === 2 && selectedClient) {
      loadAllInsights(selectedClient, dateFrom, dateTo);
    }
  }, [dateFrom, dateTo, step, selectedClient]); // eslint-disable-line

  useEffect(() => { loadClients(); }, []);

  useEffect(() => {
    if (clients.length === 0) return;
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client');
    if (clientId) {
      const found = clients.find(c => String(c.id) === clientId);
      if (found) setSelectedClient(found);
    }
  }, [clients, location.search]);

  const years = Array.from({ length: new Date().getFullYear() - 2025 }, (_, i) => 2026 + i);

  async function loadClients() {
    const { data } = await supabase.from('clients').select('*').order('name');
    if (data) { const hidden = JSON.parse(localStorage.getItem('sidebar_hidden') || '[]'); setClients(data.filter(c => !hidden.includes(c.id))); }
  }

  function toggleKpi(kpi) {
    setVisibleKpis(prev => prev.includes(kpi) ? prev.filter(k => k !== kpi) : [...prev, kpi]);
  }

  async function loadCampaigns() {
    if (!selectedClient) return;
    setLoadingCampaigns(true);
    setCampaignInsights({});
    setAdsetRows([]);
    setError(null);
    try {
      const { data: adAccounts } = await supabase
        .from('ad_accounts').select('id, platform').eq('client_id', selectedClient.id);
      if (!adAccounts?.length) { setCampaigns([]); setLoadingCampaigns(false); return; }
      const accountIds = adAccounts.map(a => a.id);
      const { data: camps, error: campsError } = await supabase
        .from('campaigns')
        .select('id, name, status, platform, ad_account_id, ad_accounts(name, platform_account_id), preview_image_url, ad_headline, ad_description, ad_final_url, start_date, end_date')
        .in('ad_account_id', accountIds)
        .neq('status', 'DELETED')
        .order('name')
        .limit(2000);
      if (campsError) { setError('Errore caricamento campagne'); setLoadingCampaigns(false); return; }
      setCampaigns(camps || []);
      setSelectedCampaigns([]);
      setCampaignInsights({});
      setAdsetRows([]);

      if (camps?.length) loadAllInsights(selectedClient, dateFrom, dateTo);
    } catch(e) {
      setError('Errore: ' + e.message);
    } finally {
      setLoadingCampaigns(false);
    }
  }

  function setShortcut(type) {
    const now = new Date();
    setActiveShortcut(type);
    if (type === 'last_month') {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      setDateFrom(from.toISOString().split('T')[0]);
      setDateTo(to.toISOString().split('T')[0]);
    } else if (type === '3months') {
      const from = new Date(now); from.setMonth(from.getMonth() - 3);
      setDateFrom(from.toISOString().split('T')[0]);
      setDateTo(now.toISOString().split('T')[0]);
    } else if (typeof type === 'number') {
      setDateFrom(`${type}-01-01`);
      setDateTo(`${type}-12-31`);
    }
  }

  function toggleCampaign(id) {
    setSelectedCampaigns(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  async function generateReport() {
    // Calcola range date reali dalle campagne selezionate
    const selectedCamps = campaigns.filter(c => selectedCampaigns.includes(c.id));
    const campStartDates = selectedCamps.map(c => c.start_date).filter(Boolean).sort();
    const campEndDates   = selectedCamps.map(c => c.end_date).filter(Boolean).sort().reverse();
    const from = campStartDates.length > 0 && campStartDates[0] < dateFrom ? campStartDates[0] : dateFrom;
    const to   = campEndDates.length > 0   && campEndDates[0]   > dateTo   ? campEndDates[0]   : dateTo;

    // Fetch adset rows SINCRONO prima di mostrare il report
    console.log('[GEN] fetching adset rows', { client: selectedClient.id, from, to });
    const { data: freshRows, error: freshErr } = await supabase.rpc('get_campaign_insights', {
      p_client_id: selectedClient.id,
      p_date_from: from,
      p_date_to:   to,
    });
    console.log('[GEN] freshRows:', freshRows?.length, '| error:', freshErr);
    // Log carosello rows
    if (freshRows) {
      const car = freshRows.filter(r => (r.adset_name||'').toLowerCase().includes('carosello'));
      const sur = freshRows.filter(r => (r.adset_name||'').toLowerCase().includes('surprise'));
      const foto = freshRows.filter(r => (r.adset_name||'').toLowerCase().includes('foto'));
      console.log('[GEN] carosello rows:', car.length, '| surprise:', sur.length, '| foto:', foto.length);
    }
    if (freshRows?.length) {
      setAdsetRows(freshRows);
      // Aggiorna anche i totali per campagna
      const freshTotals = {};
      freshRows.forEach(r => {
        const cid = r.campaign_id;
        if (!freshTotals[cid]) freshTotals[cid] = { spend:0, impressions:0, clicks:0, all_clicks:0, reach:0, inline_link_clicks:0, post_reactions:0, engagement:0 };
        freshTotals[cid].spend              += parseFloat(r.spend)||0;
        freshTotals[cid].impressions        += parseInt(r.impressions)||0;
        freshTotals[cid].clicks             += parseInt(r.inline_link_clicks)||0;
        freshTotals[cid].all_clicks         += parseInt(r.all_clicks)||0;
        freshTotals[cid].reach              += parseInt(r.reach)||0;
        freshTotals[cid].inline_link_clicks += parseInt(r.inline_link_clicks)||0;
        freshTotals[cid].post_reactions     += parseInt(r.post_reactions)||0;
        freshTotals[cid].engagement         += parseInt(r.engagement)||0;
      });
      setCampaignInsights(prev => ({ ...prev, ...freshTotals }));
    }
    setStep(3);
    setLoadingAds(true);
    try {
      const adsByCampaign = {};
      campaigns.filter(c => selectedCampaigns.includes(c.id)).forEach(c => {
        if (c.preview_image_url) {
          adsByCampaign[c.id] = {
            campaign_id: c.id,
            name: c.name,
            stored_image_url: c.preview_image_url,
            title: c.ad_headline || '',
            body: c.ad_description || '',
          };
        }
      });
      setCampaignAds(adsByCampaign);
    } catch(e) {
      console.error('Errore fetch creativi:', e);
    } finally {
      setLoadingAds(false);
    }
  }

  async function saveReport() {
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);
    try {
      const el = document.getElementById('report-output');
      const rawHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${reportTitle || 'Report'}</title><style>body{margin:0;font-family:system-ui,sans-serif;-webkit-print-color-adjust:exact}</style></head><body>${el ? el.innerHTML : ''}</body></html>`;
      const htmlContent = fixUnicode(rawHtml);
      const publicToken = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      const { error } = await supabase.from('reports').insert({
        id: crypto.randomUUID(),
        public_token: publicToken,
        cliente: selectedClient?.name || '',
        campagna: 'Report ' + dateFrom + ' \u2192 ' + dateTo,
        periodo: dateFrom + ' \u2192 ' + dateTo,
        created_at: new Date().toISOString(),
        html_content: htmlContent,
        form_data: {
          clientId: selectedClient?.id,
          templateId: selectedTemplate?.id,
          dateFrom,
          dateTo,
          selectedCampaigns,
        },
      });
      if (error) throw error;
      setSaveSuccess(publicToken);
    } catch (e) {
      setSaveError(e.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  }

  /* ─── Stepper ─── */
  const StepIndicator = ({ num, label, current, done }) => {
    const state = done ? 'done' : current ? 'active' : 'inactive';
    return (
      <div style={s.stepItem}>
        <div style={s.stepNum(state)}>
          {done
            ? <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke={C.green} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            : num}
        </div>
        <span style={s.stepLabel(state)}>{label}</span>
      </div>
    );
  };

  /* ─── Render ─── */
  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.5); } input:focus { border-color: ${C.purple} !important; }`}</style>

      <h1 style={s.title}>Genera Report</h1>
      <p style={s.subtitle}>Seleziona cliente, template e campagne</p>

      {/* Stepper */}
      <div style={s.steps}>
        <StepIndicator num={1} label="Configurazione" current={step === 1} done={step > 1} />
        <div style={s.stepLine} />
        <StepIndicator num={2} label="Campagne" current={step === 2} done={step > 2} />
        <div style={s.stepLine} />
        <StepIndicator num={3} label="Report" current={step === 3} done={false} />
      </div>

      {/* ── STEP 1 ── */}
      {step === 1 && (
        <>
          {/* Cliente */}
          <div style={s.section}>
            <span style={s.sectionLabel}>Cliente</span>
            <div style={s.chips}>
              {clients.map(c => {
                const sel = selectedClient?.id === c.id;
                return (
                  <div key={c.id} style={s.chip(sel)} onClick={() => {
                    setSelectedClient(c);
                    setSelectedTemplate(null); // reset template quando cambia cliente
                  }}>
                    <div style={s.initials(sel)}>{getInitials(c.name)}</div>
                    {c.name}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tipo Report — Template */}
          <div style={s.section}>
            <span style={s.sectionLabel}>Tipo Report</span>
            {selectedTemplate ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.purple }}>{selectedTemplate.label}</span>
                  {selectedTemplate.description && (
                    <span style={{ fontSize: 12, color: C.text2, marginLeft: 10 }}>{selectedTemplate.description}</span>
                  )}
                  {selectedTemplate.hide_economics && (
                    <span style={{ fontSize: 11, color: '#f59e0b', marginLeft: 10 }}>✦ No economics</span>
                  )}
                </div>
                <button style={s.secBtn} onClick={() => setSelectedTemplate(null)}>Cambia template</button>
              </div>
            ) : (
              <TemplateSelector
                clientId={selectedClient?.id}
                onSelect={(t) => setSelectedTemplate(t)}
              />
            )}
          </div>

          {/* Titolo + Immagine */}
          <div style={s.section}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <span style={s.sectionLabel}>Titolo <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11, color: C.text3 }}>— opzionale</span></span>
                <input style={s.titleInput} type="text" value={reportTitle}
                  onChange={e => setReportTitle(e.target.value)}
                  placeholder={`es. ${selectedTemplate?.label || 'Report'} — ${selectedClient?.name || 'Cliente'}`} />
              </div>
              <div>
                <span style={s.sectionLabel}>Immagine campagna <span style={{ color: C.text3, fontWeight: 400, fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>(opzionale)</span></span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                  <label style={{
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 16px', borderRadius: 7, border: `1px dashed ${C.border}`, background: C.bg,
                    fontSize: 12, fontWeight: 500, color: C.text2,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M7 2v10M2 7h10" strokeLinecap="round"/></svg>
                    {campaignImage ? campaignImage.name : 'Carica immagine'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setCampaignImage(e.target.files[0])} />
                  </label>
                  {campaignImage && (
                    <button style={{ ...s.secBtn, color: C.red, borderColor: C.red, padding: '6px 10px', fontSize: 12 }} onClick={() => setCampaignImage(null)}>
                      Rimuovi
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <button
            style={s.ctaBtn(!selectedClient || !selectedTemplate)}
            disabled={!selectedClient || !selectedTemplate}
            onClick={() => { setStep(2); loadCampaigns(); }}
          >
            Continua — Seleziona Campagne
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="1.5"><path d="M5 3l5 4-5 4" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </>
      )}

      {/* ── STEP 2 ── */}
      {step === 2 && (
        <>
          <div style={s.section}>
            {/* Badge template attivo */}
            {selectedTemplate && (
              <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af' }}>Template</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.purple, background: C.purpleBg, border: `1px solid #c7d2fe`, borderRadius: 6, padding: '2px 10px' }}>
                  {selectedTemplate.label}
                </span>
              </div>
            )}

            {/* Periodo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <div style={s.dateGroup}>
                <span style={s.dateLabel}>Dal</span>
                <input style={s.dateInput} type="date" value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setActiveShortcut(null); }} />
              </div>
              <span style={{ color: C.text3, fontSize: 16, lineHeight: '36px' }}>→</span>
              <div style={s.dateGroup}>
                <span style={s.dateLabel}>Al</span>
                <input style={s.dateInput} type="date" value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setActiveShortcut(null); }} />
              </div>
              <div style={{ width: 1, height: 24, background: C.border, margin: '0 4px' }} />
              <div style={s.shortcuts}>
                <button style={s.scut(activeShortcut === 'last_month')} onClick={() => setShortcut('last_month')}>Ultimo mese</button>
                <button style={s.scut(activeShortcut === '3months')} onClick={() => setShortcut('3months')}>3 mesi</button>
                {years.map(y => (
                  <button key={y} style={s.scut(activeShortcut === y)} onClick={() => setShortcut(y)}>{y}</button>
                ))}
              </div>
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9ca3af', whiteSpace: 'nowrap', lineHeight: '32px' }}>
                Campagne ({selectedCampaigns.length}/{campaigns.filter(c => c.name.toLowerCase().includes(search.toLowerCase())).length})
              </span>
              <input type="text" placeholder="Cerca campagna..." value={search}
                onChange={e => setSearch(e.target.value)} style={s.searchInput} />
              <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', alignItems: 'center' }}>
                {[{ key: 'spend', label: 'Spend' }, { key: 'impressions', label: 'Impression' }, { key: 'clicks', label: 'Click' }].map(({ key, label }) => (
                  <button key={key} style={s.filterPill(visibleKpis.includes(key))} onClick={() => toggleKpi(key)}>{label}</button>
                ))}
                <button style={s.filterPill(onlyWithData)} onClick={() => setOnlyWithData(p => !p)}>Solo con dati</button>
                <div style={{ width: 1, height: 20, background: C.border, margin: '0 4px' }} />
                <button style={s.filterPill(false)} onClick={() => setSelectedCampaigns(campaigns.map(c => c.id))}>Tutte</button>
                <button style={s.filterPill(false)} onClick={() => setSelectedCampaigns([])}>Nessuna</button>
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ padding: '0 8px', borderRadius: 6, border: `1px solid #d1d5db`, background: C.card, color: '#374151', fontSize: 11, fontFamily: font, cursor: 'pointer', height: 32 }}>
                  {[10,20,50,100,200].map(n => <option key={n} value={n}>{n} righe</option>)}
                </select>
              </div>
            </div>

            {loadingCampaigns ? (
              <div style={s.loadingBox}>
                <div style={s.spinner} />
                <span style={{ fontSize: 13, color: C.text2 }}>Caricamento campagne...</span>
              </div>
            ) : campaigns.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: C.text2, fontSize: 13 }}>
                Nessuna campagna trovata per questo cliente nel periodo selezionato.
              </div>
            ) : (
              <div style={{ borderRadius: 8, overflow: 'auto', border: `1px solid ${C.border}` }}>
                <table style={{ ...s.campaignTable, minWidth: 1060 }}>
                  <thead>
                    <tr>
                      <th style={{ ...s.tableHeader, width: 40 }}></th>
                      <th style={{ ...s.tableHeader }}>Campagna</th>
                      <th style={{ ...s.tableHeader, width: 110, textAlign: 'right' }}>Piattaforma</th>
                      <th style={{ ...s.tableHeader, width: 220, textAlign: 'right' }}>AdAccount</th>
                      <th style={{ ...s.tableHeader, width: 90, textAlign: 'right' }}>Inizio</th>
                      <th style={{ ...s.tableHeader, width: 90, textAlign: 'right' }}>Fine</th>
                      <th style={{ ...s.tableHeader, width: 60, textAlign: 'center' }}>Img</th>
                      {visibleKpis.includes('spend') && <th style={{ ...s.tableHeader, width: 110, textAlign: 'right' }}>Spend</th>}
                      {visibleKpis.includes('impressions') && <th style={{ ...s.tableHeader, width: 120, textAlign: 'right' }}>Impression</th>}
                      {visibleKpis.includes('clicks') && <th style={{ ...s.tableHeader, width: 90, textAlign: 'right' }}>Click</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCampaigns.map(c => {
                      const checked = selectedCampaigns.includes(c.id);
                      return (
                        <tr key={c.id} style={s.campaignRow(checked)} onClick={() => toggleCampaign(c.id)}>
                          <td style={{ ...s.campaignCell, paddingLeft: 12, width: 40 }}>
                            <div style={s.checkBox(checked)}>
                              {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                            </div>
                          </td>
                          <td style={s.campaignName}>{c.name}</td>
                          <td style={{ ...s.campaignCell, textAlign: 'right' }}>
                            <span style={s.platformBadge(c.platform)}>{c.platform}</span>
                          </td>
                          <td style={{ ...s.campaignCell, fontSize: 11, color: C.text3, textAlign: 'right' }}>
                            {c.ad_accounts?.name || c.ad_account_id?.substring(0,8)}
                          </td>
                          <td style={{ ...s.campaignCell, textAlign: 'right', fontSize: 11, color: C.text3 }}>
                            {c.start_date ? new Date(c.start_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                          </td>
                          <td style={{ ...s.campaignCell, textAlign: 'right', fontSize: 11, color: C.text3 }}>
                            {c.end_date ? new Date(c.end_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 'in corso'}
                          </td>
                          <td style={{ ...s.campaignCell, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            {uploadingImage === c.id ? (
                              <span style={{ fontSize: 10, color: C.text3 }}>...</span>
                            ) : c.preview_image_url ? (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <img src={c.preview_image_url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                                  onError={e => e.target.style.display='none'}/>
                                <label style={{ position: 'absolute', inset: 0, cursor: 'pointer', opacity: 0 }} title="Sostituisci immagine">
                                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleImageUpload(c, e.target.files[0])}/>
                                </label>
                              </div>
                            ) : (
                              <label style={{
                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 40, height: 40, borderRadius: 4, border: `1px dashed ${C.border}`,
                                color: C.text3, fontSize: 14, lineHeight: 1, background: C.bg,
                              }} title="Carica immagine">
                                +
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleImageUpload(c, e.target.files[0])}/>
                              </label>
                            )}
                          </td>
                          {visibleKpis.includes('spend') && <td style={{ ...s.numericCell, color: (campaignInsights[c.id]?.spend || 0) > 0 ? C.text : C.text3 }}>{loadingInsights ? '...' : formatCurrency(campaignInsights[c.id]?.spend || 0)}</td>}
                          {visibleKpis.includes('impressions') && <td style={s.numericCell}>{loadingInsights ? '...' : formatNumber(campaignInsights[c.id]?.impressions || 0)}</td>}
                          {visibleKpis.includes('clicks') && <td style={s.numericCell}>{loadingInsights ? '...' : formatNumber(campaignInsights[c.id]?.clicks || 0)}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {error && (
            <div style={{ color: C.red, fontSize: 13, marginBottom: 10, padding: '10px 14px', background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button style={{ ...s.secBtn, flex: 1 }} onClick={() => setStep(1)}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginRight: 4 }}><path d="M7 2L3 6l4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Indietro
            </button>
            <button style={{ ...s.primBtn, flex: 2, opacity: selectedCampaigns.length === 0 ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              disabled={selectedCampaigns.length === 0}
              onClick={() => { setStep(3); generateReport(); }}>
              Genera Report
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="1.5"><path d="M5 2l4 4-4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </>
      )}

      {/* ── STEP 3 ── */}
      {step === 3 && (
        <>
          {loadingReport || loadingAds ? (
            <div style={{ ...s.section, ...s.loadingBox }}>
              <div style={s.spinner} />
              <span style={{ fontSize: 14, color: C.text2, fontWeight: 500 }}>{loadingAds ? 'Caricamento creativi...' : 'Generazione report...'}</span>
              <span style={{ fontSize: 12, color: C.text3 }}>{selectedCampaigns.length} campagne · {selectedClient?.name} · {selectedTemplate?.label}</span>
            </div>
          ) : error ? (
            <div style={{ ...s.section, color: C.red }}>{error}</div>
          ) : (
            <>
              <div style={s.section}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 2 }}>
                      {reportTitle || `${selectedTemplate?.label} — ${selectedClient?.name}`}
                    </div>
                    <div style={{ fontSize: 12, color: C.text2 }}>
                      {dateFrom} → {dateTo} · {selectedCampaigns.length} campagne
                      {selectedTemplate && <span style={{ marginLeft: 8, color: C.purple, fontWeight: 600 }}>· {selectedTemplate.label}</span>}
                    </div>
                  </div>
                </div>
                <div id="report-output">
                  <ReportTemplate
                    client={selectedClient}
                    template={selectedTemplate}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    campaigns={campaigns.filter(c => selectedCampaigns.includes(c.id))}
                    campaignInsights={campaignInsights}
                    campaignAds={campaignAds}
                    dailyData={dailyData}
                    mockupImageUrl={mockupImageUrl || (() => {
                      const withImg = campaigns.filter(c => selectedCampaigns.includes(c.id) && c.preview_image_url);
                      const withSpend = campaigns.filter(c => selectedCampaigns.includes(c.id) && (campaignInsights[c.id]?.spend||0) > 0 && c.preview_image_url);
                      return (withSpend[0] || withImg[0])?.preview_image_url || null;
                    })()}
                    demographics={demographics}
                    adsetRows={adsetRows}
                  />
                </div>
              </div>

              <div style={s.actionRow}>
                <button style={s.secBtn} onClick={() => { setStep(2); setReport(''); setMockupImageUrl(null); }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginRight: 4 }}><path d="M7 2L3 6l4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Modifica selezione
                </button>
                <button style={s.secBtn} onClick={() => { setStep(1); setReport(''); setSelectedClient(null); setSelectedCampaigns([]); setSelectedTemplate(null); }}>Nuovo report</button>
                <button style={{ ...s.secBtn, background: C.greenBg, borderColor: C.green, color: C.green }} onClick={() => window.print()}>Stampa / Salva PDF</button>
                <button style={s.primBtn} onClick={() => {
                  const el = document.getElementById('report-output');
                  const html = fixUnicode(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${reportTitle || 'Report'}</title><link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap" rel="stylesheet"><style>body{margin:0;font-family:'Montserrat',sans-serif;-webkit-print-color-adjust:exact}</style></head><body>${el ? el.innerHTML : ''}</body></html>`);
                  const blob = new Blob([html], { type: 'text/html' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url;
                  a.download = (reportTitle || selectedClient?.name || 'report') + '-' + dateFrom + '.html'; a.click();
                }}>Scarica HTML</button>
                <button style={{ ...s.primBtn, background: '#059669' }} disabled={saving} onClick={saveReport}>
                  {saving ? 'Salvataggio...' : 'Salva Report'}
                </button>
              </div>
              {saveSuccess && (
                <div style={{ marginTop: 12, padding: '12px 16px', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, fontSize: 13, color: '#065f46' }}>
                  Report salvato! <a href={`https://cloud.gallicantus.it/reportradio/r/${saveSuccess}`} target="_blank" rel="noopener noreferrer" style={{ color: '#059669', fontWeight: 600 }}>Apri link pubblico</a>
                </div>
              )}
              {saveError && (
                <div style={{ marginTop: 12, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#991b1b' }}>
                  Errore: {saveError}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
